import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadGenService } from '../../services/leadGeneration';
import { crawlingService } from '../../services/crawlingService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';
import ConfirmationModal from '../common/ConfirmationModal';
import RecrawlButton from '../crawling/RecrawlButton';
import BatchRecrawlModal from '../crawling/BatchRecrawlModal';
// import CrawlingProgressMonitor from '../crawling/CrawlingProgressMonitor';
import GuideSystem from '../common/GuideSystem';
import FloatingGuideButton from '../common/FloatingGuideButton';
import { getGuideContent } from '../../utils/guideContent';
import { useGuide } from '../../hooks/useGuide';
import { useCrawlUpdates } from '../../hooks/useCrawlUpdates';

const SavedPlaces = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [places, setPlaces] = useState([]);
  const [placesWithLinks, setPlacesWithLinks] = useState({});
  const [crawlStatuses, setCrawlStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [showBatchRecrawl, setShowBatchRecrawl] = useState(false);
  const [activeCrawlTasks, setActiveCrawlTasks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(new Set());
  const [expandedPlaces, setExpandedPlaces] = useState(new Set());
  const [filters, setFilters] = useState({
    search: '',
    hasLead: '',
    hasWebsite: '',
    hasSocialLinks: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
  });
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'default'
  });

  // Guide system integration
  const { isGuideVisible, hasSeenGuide, showGuide, hideGuide } = useGuide('places');
  const guideData = getGuideContent('places');

  // WebSocket real-time updates
  const {
    isConnected: wsConnected,
    crawlStatuses: realtimeCrawlStatuses,
    crawlProgress: realtimeCrawlProgress,
    linksByPlace: realtimeLinks,
    subscribePlaces,
    getPlaceStatus,
    getPlaceProgress,
    getPlaceLinks
  } = useCrawlUpdates();

  useEffect(() => {
    loadPlaces();
  }, [filters, pagination.page]);

  // Subscribe to places for WebSocket updates
  useEffect(() => {
    if (places.length > 0 && wsConnected) {
      const placeIds = places.map(place => place.id);
      subscribePlaces(placeIds);
    }
  }, [places, wsConnected]);

  // Load links for all places with social links
  useEffect(() => {
    if (places.length > 0) {
      places.forEach(place => {
        if (place.social_links_count > 0 && !placesWithLinks[place.id]) {
          loadSocialLinksForPlace(place.id);
        }
      });
    }
  }, [places]);

  // Merge real-time links with loaded links
  useEffect(() => {
    if (Object.keys(realtimeLinks).length > 0) {
      setPlacesWithLinks(prev => {
        const updated = { ...prev };
        Object.keys(realtimeLinks).forEach(placeId => {
          if (realtimeLinks[placeId].length > 0) {
            const existingLinks = prev[placeId] || [];
            const existingUrls = new Set(existingLinks.map(l => l.url));
            const newLinks = realtimeLinks[placeId].filter(l => !existingUrls.has(l.url));
            
            if (newLinks.length > 0) {
              updated[placeId] = [...existingLinks, ...newLinks];
            }
          }
        });
        return updated;
      });
    }
  }, [realtimeLinks]);

  // Update places when crawl completes
  useEffect(() => {
    Object.entries(realtimeCrawlStatuses).forEach(([placeId, status]) => {
      if (status.status === 'completed') {
        const place = places.find(p => p.id === placeId);
        if (place) {
          setPlaces(prevPlaces => 
            prevPlaces.map(p => 
              p.id === placeId 
                ? { ...p, social_links_count: status.links_found || p.social_links_count }
                : p
            )
          );
          
          // Reload links for this place
          loadSocialLinksForPlace(placeId);
        }
      }
    });
  }, [realtimeCrawlStatuses]);

  // Merge real-time crawl statuses with backend statuses
  useEffect(() => {
    if (Object.keys(realtimeCrawlStatuses).length > 0) {
      setCrawlStatuses(prev => {
        const updated = { ...prev };
        Object.keys(realtimeCrawlStatuses).forEach(placeId => {
          const realtimeStatus = realtimeCrawlStatuses[placeId];
          
          updated[placeId] = {
            ...prev[placeId],
            latest_crawl_task: {
              ...prev[placeId]?.latest_crawl_task,
              status: realtimeStatus.status,
              error_message: realtimeStatus.error_message,
            },
            can_recrawl: ['completed', 'failed', 'cancelled'].includes(realtimeStatus.status),
            last_crawled: realtimeStatus.completed_at || realtimeStatus.failed_at,
            total_links: realtimeStatus.links_found || prev[placeId]?.total_links || 0
          };
        });
        return updated;
      });
    }
  }, [realtimeCrawlStatuses]);

  const loadPlaces = async () => {
    setLoading(true);
    const params = {
      page: pagination.page,
      page_size: pagination.page_size,
      search: filters.search,
      has_lead: filters.hasLead,
      has_website: filters.hasWebsite,
      has_social_links: filters.hasSocialLinks,
    };

    Object.keys(params).forEach(key => {
      if (params[key] === '') delete params[key];
    });

    const { data, error } = await leadGenService.getUserPlaces(params);
    
    if (data) {
      setPlaces(data.places || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
      }));
      
      // Load statuses immediately after places load
      if (data.places && data.places.length > 0) {
        const placeIds = data.places.map(p => p.id);
        const { data: statusData } = await crawlingService.getBulkCrawlStatus(placeIds);
        if (statusData && statusData.results) {
          setCrawlStatuses(statusData.results);
        }
      }
    } else if (error) {
      toast.error('Failed to load saved places');
      console.error('Error loading places:', error);
    }
    
    setLoading(false);
  };

  const loadSocialLinksForPlace = async (placeId) => {
    if (loadingLinks.has(placeId)) return;
    
    setLoadingLinks(prev => new Set(prev).add(placeId));
    
    try {
      const { data } = await crawlingService.getSocialMediaLinks({
        place_id: placeId,
        page_size: 50
      });
      
      if (data && data.results) {
        setPlacesWithLinks(prev => ({
          ...prev,
          [placeId]: data.results
        }));
      }
    } catch (error) {
      console.error('Error loading social links:', error);
    } finally {
      setLoadingLinks(prev => {
        const newSet = new Set(prev);
        newSet.delete(placeId);
        return newSet;
      });
    }
  };

  const handleSelectPlace = (placeId) => {
    setSelectedPlaces(prev => {
      if (prev.includes(placeId)) {
        return prev.filter(id => id !== placeId);
      } else {
        return [...prev, placeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPlaces.length === places.length) {
      setSelectedPlaces([]);
    } else {
      setSelectedPlaces(places.map(place => place.id));
    }
  };

  const deleteSelectedPlaces = async () => {
    if (selectedPlaces.length === 0) {
      toast.error('Please select places to delete');
      return;
    }

    setConfirmationModal({
      isOpen: true,
      title: 'Delete Places',
      message: `Delete ${selectedPlaces.length} selected place(s)? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        const { data, error } = await leadGenService.deletePlaces(selectedPlaces);
        
        if (data) {
          toast.success(`Deleted ${selectedPlaces.length} places`);
          setSelectedPlaces([]);
          loadPlaces();
        } else {
          toast.error('Failed to delete places');
        }
        
        setConfirmationModal({ ...confirmationModal, isOpen: false });
      }
    });
  };

  const convertToLeads = async () => {
    if (selectedPlaces.length === 0) {
      toast.error('Please select places to convert to leads');
      return;
    }

    const { data, error } = await leadGenService.createMultipleLeads(selectedPlaces);
    
    if (data) {
      toast.success(`Converted ${selectedPlaces.length} places to leads`);
      setSelectedPlaces([]);
      loadPlaces();
    } else {
      toast.error('Failed to convert places to leads');
    }
  };

  const exportPlaces = async () => {
    const { data, error } = await leadGenService.exportPlaces();
    
    if (data) {
      toast.success(`Exported places to ${data.filename}`);
    } else {
      toast.error('Failed to export places');
    }
  };

  const handleCrawlStarted = (crawlTask) => {
    setActiveCrawlTasks(prev => [...prev, crawlTask.id]);
  };

  const handleCrawlComplete = (crawlTask) => {
    setActiveCrawlTasks(prev => prev.filter(id => id !== crawlTask.id));
    loadPlaces();
  };

  const handleBatchCrawlStart = (batchData) => {
    const newTaskIds = batchData.details.queued.map(item => item.crawl_task_id);
    setActiveCrawlTasks(prev => [...prev, ...newTaskIds]);
    setSelectedPlaces([]);
    setShowBatchRecrawl(false);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const openLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getSocialLinkIcon = (linkType) => {
    const icons = {
      facebook: '📘',
      instagram: '📷', 
      twitter: '🐦',
      linkedin: '💼',
      youtube: '🎥',
      tiktok: '🎵',
      whatsapp: '📱',
      email: '📧',
      phone: '📞',
      website: '🌐'
    };
    return icons[linkType] || '🔗';
  };

  const getSocialLinkColor = (linkType) => {
    const colors = {
      facebook: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      instagram: 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100',
      twitter: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',
      linkedin: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      youtube: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
      tiktok: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',
      whatsapp: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
      email: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',
      phone: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',
      website: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
    };
    return colors[linkType] || 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100';
  };

  const extractContactContent = (url, linkType) => {
    if (linkType === 'email') {
      if (url.startsWith('mailto:')) {
        return url.replace('mailto:', '');
      }
      if (url.includes('@') && !url.includes('http')) {
        return url;
      }
    }
    
    if (linkType === 'phone') {
      if (url.startsWith('tel:')) {
        return url.replace('tel:', '');
      }
      if (/^[\+]?[0-9\(\)\-\s]+$/.test(url)) {
        return url;
      }
    }
    
    return null;
  };

  const renderSocialLinks = (place) => {
  const links = placesWithLinks[place.id] || [];
  const isLoadingForPlace = loadingLinks.has(place.id);
  const progress = getPlaceProgress(place.id);
  const status = getPlaceStatus(place.id);
  const crawlStatus = crawlStatuses[place.id];
  const isExpanded = expandedPlaces.has(place.id);

  const isPending = crawlStatus?.latest_crawl_task?.status === 'pending';
  
  if (isPending) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="p-2 bg-blue-50 rounded border border-blue-200">
          <div className="flex items-center justify-between text-xs">
            <span className="text-blue-700 font-medium">🚀 Starting crawl...</span>
            <div className="animate-pulse text-blue-600">●●●</div>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-1.5 mt-1">
            <div className="bg-blue-600 h-1.5 rounded-full animate-pulse w-1/4" />
          </div>
        </div>
      </div>
    );
  }

  if (progress && status?.status === 'processing') {
    return (
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="p-2 bg-blue-50 rounded border border-blue-200">
          <div className="flex items-center justify-between text-xs">
            <span className="text-blue-700 font-medium">🔄 {progress.current_step}</span>
            <span className="text-blue-600">{Math.round(progress.percentage)}%</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-1.5 mt-1">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  const displayLinks = isExpanded ? links : links.slice(0, 8);
  const hasMore = links.length > 8;

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      {isLoadingForPlace ? (
        <div className="flex items-center justify-center py-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-xs text-gray-600">Loading...</span>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 select-none">
            {place.website_uri && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openLink(place.website_uri);
                }}
                className={`flex items-center space-x-1 px-2 py-1 rounded border text-xs hover:shadow-sm transition-all ${getSocialLinkColor('website')}`}
                title={place.website_uri}
              >
                <span className="text-sm">{getSocialLinkIcon('website')}</span>
                <span className="font-medium capitalize">Website</span>
              </button>
            )}
            
            {displayLinks.length > 0 && displayLinks.sort((a, b) => a.link_type === 'email' ? 1 : b.link_type === 'email' ? -1 : 0).map((link) => {
              const contactContent = extractContactContent(link.url, link.link_type);
              
              return (
                <button
                  key={link.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    openLink(link.url);
                  }}
                  className={`flex items-center space-x-1 px-2 py-1 rounded border text-xs hover:shadow-sm transition-all ${getSocialLinkColor(link.link_type)}`}
                  title={link.url}
                >
                  <span className="text-sm">{getSocialLinkIcon(link.link_type)}</span>
                  <span className="font-medium capitalize">
                    {link.link_type === 'email' ? extractContactContent(link.url, link.link_type) || link.link_type : link.link_type}
                  </span>
                  {link.verification_status === 'verified' && (
                    <span className="text-xs">✓</span>
                  )}
                </button>
              );
            })}
            
            {!place.website_uri && links.length === 0 && (
              <p className="text-xs text-gray-400">No links found</p>
            )}
          </div>
          
          {hasMore && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedPlaces(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(place.id)) {
                    newSet.delete(place.id);
                  } else {
                    newSet.add(place.id);
                  }
                  return newSet;
                });
              }}
              className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1"
            >
              <span>{isExpanded ? '↑ Show Less' : `↓ Show ${links.length - 8} More`}</span>
            </button>
          )}
        </>
      )}
    </div>
  );
};

  const totalPages = Math.ceil(pagination.total / pagination.page_size);

  if (loading && places.length === 0) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {guideData && (
        <GuideSystem
          steps={guideData.steps}
          pageName={guideData.pageName}
          isVisible={isGuideVisible}
          onClose={hideGuide}
        />
      )}

      <FloatingGuideButton 
        onClick={showGuide}
        hasNewFeature={!hasSeenGuide}
      />

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
      />

      <BatchRecrawlModal
        isOpen={showBatchRecrawl}
        onClose={() => setShowBatchRecrawl(false)}
        places={places.filter(place => selectedPlaces.includes(place.id))}
        onBatchStart={handleBatchCrawlStart}
      />

      <div className="bg-white shadow-sm border-b">
        <div className="max-w-screen mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              {/* Home Icon */}
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Go to Dashboard"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </button>
            </div>
            
            <div className="flex items-center space-x-3 select-none">
              <button
                onClick={exportPlaces}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Export to CSV</span>
              </button>
              {selectedPlaces.length > 0 && (
                <>
                  <button
                    onClick={() => setShowBatchRecrawl(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                  >
                    Crawl Selected ({selectedPlaces.length})
                  </button>
                  <button
                    onClick={convertToLeads}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  >
                    Convert to Leads ({selectedPlaces.length})
                  </button>
                  <button
                    onClick={deleteSelectedPlaces}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                  >
                    Delete ({selectedPlaces.length})
                  </button>
                </>
              )}
              <button
                onClick={() => navigate('/search')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Search More Places
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-lg font-semibold text-gray-900">{pagination.total}</div>
              <div className="text-sm text-gray-500">Total Places</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-green-600">
                {places.filter(p => p.has_lead).length}
              </div>
              <div className="text-sm text-gray-500">Already Leads</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-blue-600">
                {places.filter(p => p.social_links_count > 0).length}
              </div>
              <div className="text-sm text-gray-500">Have Social Links</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-purple-600">
                {places.filter(p => p.website_uri).length}
              </div>
              <div className="text-sm text-gray-500">Have Websites</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-orange-600">
                {places.filter(p => p.rating >= 4.0).length}
              </div>
              <div className="text-sm text-gray-500">High Rated (4.0+)</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 select-none">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search places..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Lead Status
              </label>
              <select
                value={filters.hasLead}
                onChange={(e) => setFilters(prev => ({ ...prev, hasLead: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm select-none"
              >
                <option value="">All Places</option>
                <option value="false">Not Yet Converted</option>
                <option value="true">Already Leads</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Social Links
              </label>
              <select
                value={filters.hasSocialLinks}
                onChange={(e) => setFilters(prev => ({ ...prev, hasSocialLinks: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm select-none"
              >
                <option value="">All</option>
                <option value="true">Has Social Links</option>
                <option value="false">No Social Links</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Website
              </label>
              <select
                value={filters.hasWebsite}
                onChange={(e) => setFilters(prev => ({ ...prev, hasWebsite: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm select-none"
              >
                <option value="">All</option>
                <option value="true">Has Website</option>
                <option value="false">No Website</option>
              </select>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <button
              onClick={() => {
                setFilters({ search: '', hasLead: '', hasWebsite: '', hasSocialLinks: '' });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={places.length > 0 && selectedPlaces.length === places.length}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-500 select-none">
                {selectedPlaces.length > 0 ? `${selectedPlaces.length} selected` : 'Select all for batch actions'}
              </span>
            </div>
            <div className="text-sm text-gray-500 select-none">
              Page {pagination.page} of {totalPages}
            </div>
          </div>

          {places.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No places found</h3>
              <p className="text-gray-500 mb-4">Start by searching for places on the map.</p>
              <button
                onClick={() => navigate('/search')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Search for Places
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {places.map((place) => (
                <div 
                  key={place.id} 
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={selectedPlaces.includes(place.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectPlace(place.id);
                      }}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    
                    <div className="ml-4 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-medium text-gray-900">
                              {place.display_name}
                            </h4>
                            
                            <div className="flex items-center space-x-2 select-none">
                              {place.rating && (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  place.rating >= 4.5 ? 'bg-green-100 text-green-800' :
                                  place.rating >= 4.0 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  ⭐ {place.rating}
                                </span>
                              )}
                              
                              {place.business_status === 'OPERATIONAL' ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✅ Open
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  ❌ Closed
                                </span>
                              )}
                              
                              {place.has_lead && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  🎯 Already Lead
                                </span>
                              )}
                              
                              {place.social_links_count > 0 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  🔗 {place.social_links_count} Links
                                </span>
                              )}

                              {(() => {
                                const status = getPlaceStatus(place.id);
                                if (status?.status === 'processing') {
                                  return (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 animate-pulse">
                                      🔄 Crawling...
                                    </span>
                                  );
                                } else if (status?.status === 'failed') {
                                  return (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      ❌ Crawl Failed
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                          
                          {place.formatted_address && (
                            <p className="text-sm text-gray-600 mt-1 flex items-center">
                              📍 {place.formatted_address}
                            </p>
                          )}

                          <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
                            {place.international_phone_number && (
                              <a 
                                href={`tel:${place.international_phone_number}`} 
                                className="text-blue-600 hover:underline flex items-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                📞 {place.international_phone_number}
                              </a>
                            )}
                            
                            {place.user_rating_count && (
                              <span className="text-gray-600 flex items-center">
                                👥 {place.user_rating_count} reviews
                              </span>
                            )}
                          </div>

                          {renderSocialLinks(place)}
                        </div>
                        
                        <div className="ml-4 flex-shrink-0 flex flex-col space-y-2" onClick={(e) => e.stopPropagation()}>
                          <RecrawlButton
                            place={place}
                            crawlStatus={crawlStatuses[place.id]}
                            onCrawlStarted={(crawlTask) => {
                              setPlacesWithLinks(prev => {
                                const updated = { ...prev };
                                delete updated[place.id];
                                return updated;
                              });
                              handleCrawlStarted(crawlTask);
                            }}
                            onCrawlComplete={handleCrawlComplete} 
                            onCrawlFailed={handleCrawlComplete}
                            size="sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.page_size) + 1} to{' '}
                  {Math.min(pagination.page * pagination.page_size, pagination.total)} of{' '}
                  {pagination.total} places
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                  >
                    ← Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + Math.max(1, pagination.page - 2);
                    if (page > totalPages) return null;
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 border rounded text-sm ${
                          page === pagination.page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedPlaces;